import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Key, Code2, Loader2, Boxes, Sparkles, ShieldCheck } from "lucide-react";

interface PlatformApp {
  id: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  theme_color: string | null;
  created_at: string;
}

interface ApiKey {
  id: string;
  app_id: string;
  key: string;
  name: string;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
}

function genApiKey() {
  const arr = new Uint8Array(24);
  crypto.getRandomValues(arr);
  const hex = Array.from(arr).map((b) => b.toString(16).padStart(2, "0")).join("");
  return `mh_live_${hex}`;
}

export default function PlatformApps() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState<PlatformApp[]>([]);
  const [keys, setKeys] = useState<Record<string, ApiKey[]>>({});
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newColor, setNewColor] = useState("#0f766e");

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user) loadAll();
  }, [user]);

  async function loadAll() {
    setLoading(true);
    const { data: appsData, error } = await supabase
      .from("apps")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast.error(error.message);
      setLoading(false);
      return;
    }
    setApps(appsData || []);

    if (appsData && appsData.length > 0) {
      const { data: keysData } = await supabase
        .from("api_keys")
        .select("*")
        .in("app_id", appsData.map((a) => a.id))
        .order("created_at", { ascending: false });
      const grouped: Record<string, ApiKey[]> = {};
      (keysData || []).forEach((k) => {
        if (!grouped[k.app_id]) grouped[k.app_id] = [];
        grouped[k.app_id].push(k as ApiKey);
      });
      setKeys(grouped);
    } else {
      setKeys({});
    }
    setLoading(false);
  }

  async function createApp() {
    if (!newName.trim() || !user) return;
    const { data, error } = await supabase
      .from("apps")
      .insert({
        owner_id: user.id,
        name: newName.trim(),
        description: newDesc.trim() || null,
        theme_color: newColor,
      })
      .select()
      .single();
    if (error) return toast.error(error.message);

    const key = genApiKey();
    await supabase.from("api_keys").insert({
      app_id: data.id,
      key,
      name: "Default key",
    });

    toast.success("App created");
    setCreateOpen(false);
    setNewName("");
    setNewDesc("");
    setNewColor("#0f766e");
    loadAll();
  }

  async function rotateKey(appId: string) {
    const key = genApiKey();
    const { error } = await supabase.from("api_keys").insert({
      app_id: appId,
      key,
      name: "Key " + new Date().toLocaleDateString(),
    });
    if (error) return toast.error(error.message);
    toast.success("New key generated");
    loadAll();
  }

  async function revokeKey(keyId: string) {
    const { error } = await supabase.from("api_keys").update({ is_active: false }).eq("id", keyId);
    if (error) return toast.error(error.message);
    toast.success("Key revoked");
    loadAll();
  }

  async function deleteApp(appId: string) {
    if (!confirm("Delete this app and all its API keys?")) return;
    const { error } = await supabase.from("apps").delete().eq("id", appId);
    if (error) return toast.error(error.message);
    toast.success("App deleted");
    loadAll();
  }

  const copy = (text: string, label = "Copied") => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const stats = useMemo(
    () => ({
      apps: apps.length,
      activeKeys: Object.values(keys).flat().filter((key) => key.is_active).length,
      recentKeys: Object.values(keys).flat().filter((key) => key.last_used_at).length,
    }),
    [apps, keys]
  );

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,hsl(var(--primary)/0.08),transparent_30%),linear-gradient(180deg,hsl(var(--background)),hsl(var(--muted)/0.22))] flex flex-col">
      <Navigation />
      <main className="flex-1 container mx-auto max-w-6xl px-4 py-8 pt-24">
        <section className="rounded-3xl border border-border/60 bg-background/85 p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Developer platform</p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight">Create apps, manage keys, and ship integrations with less friction.</h1>
              <p className="mt-3 text-muted-foreground">
                This area is for teams embedding MeetHub into their own products. Keep each app isolated, rotate keys cleanly, and copy docs without hunting around.
              </p>
            </div>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" /> New app
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create app</DialogTitle>
                  <DialogDescription>
                    Each app gets its own API key for creating and managing meetings.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>App name</Label>
                    <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="My SaaS" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Optional" />
                  </div>
                  <div>
                    <Label>Theme color</Label>
                    <div className="flex gap-2 items-center">
                      <Input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="w-20 h-10" />
                      <Input value={newColor} onChange={(e) => setNewColor(e.target.value)} />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={createApp} disabled={!newName.trim()}>
                    Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </section>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <SummaryCard label="Apps" value={String(stats.apps)} helper="Connected products" />
          <SummaryCard label="Active keys" value={String(stats.activeKeys)} helper="Live credentials" />
          <SummaryCard label="Used keys" value={String(stats.recentKeys)} helper="Keys with usage history" />
        </div>

        <Tabs defaultValue="apps" className="mt-8">
          <TabsList className="grid w-full max-w-xl grid-cols-3">
            <TabsTrigger value="apps">My apps</TabsTrigger>
            <TabsTrigger value="docs">API docs</TabsTrigger>
            <TabsTrigger value="sdk">JS SDK</TabsTrigger>
          </TabsList>

          <TabsContent value="apps" className="mt-6 space-y-4">
            {apps.length === 0 ? (
              <Card className="border-border/60 shadow-sm">
                <CardContent className="py-14 text-center">
                  <Boxes className="mx-auto h-10 w-10 text-muted-foreground" />
                  <p className="mt-4 text-lg font-medium">No apps yet</p>
                  <p className="mt-2 text-sm text-muted-foreground">Create your first app to start issuing API keys and embedding MeetHub.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 xl:grid-cols-2">
                {apps.map((app) => (
                  <Card key={app.id} className="border-border/60 shadow-sm">
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div
                            className="flex h-11 w-11 items-center justify-center rounded-2xl text-white font-semibold shadow-sm"
                            style={{ backgroundColor: app.theme_color || "#0f766e" }}
                          >
                            {app.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <CardTitle className="text-lg">{app.name}</CardTitle>
                            <CardDescription className="mt-1 text-sm">
                              {app.description || "No description added yet."}
                            </CardDescription>
                            <p className="mt-2 text-xs text-muted-foreground">
                              Created {new Date(app.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => deleteApp(app.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs flex items-center gap-1 uppercase tracking-[0.16em] text-muted-foreground">
                          <Key className="h-3 w-3" /> API Keys
                        </Label>
                        <Button variant="outline" size="sm" onClick={() => rotateKey(app.id)}>
                          Generate new key
                        </Button>
                      </div>

                      {(keys[app.id] || []).map((k) => (
                        <div key={k.id} className="rounded-2xl border border-border/60 bg-muted/20 p-3">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <Badge variant={k.is_active ? "default" : "secondary"}>{k.is_active ? "Active" : "Revoked"}</Badge>
                                {k.last_used_at && (
                                  <span className="text-xs text-muted-foreground">
                                    Last used {new Date(k.last_used_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                              <p className="mt-2 truncate font-mono text-xs">{k.key}</p>
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <Button variant="ghost" size="icon" onClick={() => copy(k.key, "API key copied")}>
                                <Copy className="h-3 w-3" />
                              </Button>
                              {k.is_active && (
                                <Button variant="ghost" size="icon" onClick={() => revokeKey(k.id)}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      {(keys[app.id] || []).length === 0 && (
                        <p className="text-sm text-muted-foreground">No keys yet.</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="docs" className="mt-6">
            <ApiDocs />
          </TabsContent>

          <TabsContent value="sdk" className="mt-6">
            <SdkDocs />
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}

const API_BASE = "https://wdshnonjtrfhreccaqul.supabase.co/functions/v1/v1-api";
const PUBLIC_BASE = "https://meethub-live.lovable.app";

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-2xl border border-border/60 bg-muted/20 p-4 text-xs">
      <code>{children}</code>
    </pre>
  );
}

function SummaryCard({ label, value, helper }: { label: string; value: string; helper: string }) {
  return (
    <Card className="border-border/60 shadow-sm">
      <CardContent className="p-5">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <p className="mt-2 text-3xl font-semibold tracking-tight">{value}</p>
        <p className="mt-1 text-xs text-muted-foreground">{helper}</p>
      </CardContent>
    </Card>
  );
}

function ApiDocs() {
  return (
    <Card className="border-border/60 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Code2 className="h-4 w-4" /> REST API
        </CardTitle>
        <CardDescription>
          Authenticate every request with <code>Authorization: Bearer &lt;api_key&gt;</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 text-sm">
        <div>
          <h3 className="mb-2 font-semibold">Create meeting</h3>
          <CodeBlock>{`POST ${API_BASE}/meetings
Authorization: Bearer mh_live_...
Content-Type: application/json

{
  "title": "Team Sync",
  "description": "Weekly catch-up",
  "start_time": "2025-01-15T10:00:00Z",
  "duration": 60,
  "visibility": "private"
}`}</CodeBlock>
        </div>

        <div>
          <h3 className="mb-2 font-semibold">List meetings</h3>
          <CodeBlock>{`GET ${API_BASE}/meetings
Authorization: Bearer mh_live_...`}</CodeBlock>
        </div>

        <div>
          <h3 className="mb-2 font-semibold">Get meeting</h3>
          <CodeBlock>{`GET ${API_BASE}/meetings/{meeting_id}
Authorization: Bearer mh_live_...`}</CodeBlock>
        </div>

        <div>
          <h3 className="mb-2 font-semibold">Start / End meeting</h3>
          <CodeBlock>{`POST ${API_BASE}/meetings/{meeting_id}/start
POST ${API_BASE}/meetings/{meeting_id}/end
Authorization: Bearer mh_live_...`}</CodeBlock>
        </div>

        <div>
          <h3 className="mb-2 font-semibold">Issue join token (15 min JWT)</h3>
          <CodeBlock>{`POST ${API_BASE}/meetings/{meeting_id}/join-token
Authorization: Bearer mh_live_...
Content-Type: application/json

{ "user_name": "Alice", "role": "participant" }

-> { "token": "...", "expires_in": 900, "meeting_url": "${PUBLIC_BASE}/embed/{code}?token=..." }`}</CodeBlock>
        </div>

        <div>
          <h3 className="mb-2 font-semibold">Embed in iframe</h3>
          <CodeBlock>{`<iframe
  src="${PUBLIC_BASE}/embed/{meeting_code}?token={join_token}"
  width="100%"
  height="600"
  allow="camera; microphone; fullscreen; display-capture; autoplay">
</iframe>`}</CodeBlock>
        </div>
      </CardContent>
    </Card>
  );
}

function SdkDocs() {
  return (
    <Card className="border-border/60 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-4 w-4" />
          JavaScript SDK
        </CardTitle>
        <CardDescription>Drop-in script with a straightforward hosted-room workflow.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div>
          <h3 className="mb-2 font-semibold">Install</h3>
          <CodeBlock>{`<script src="${PUBLIC_BASE}/sdk/meethub.js"></script>`}</CodeBlock>
        </div>
        <div>
          <h3 className="mb-2 font-semibold">Initialize</h3>
          <CodeBlock>{`MeetHub.init({ apiKey: "mh_live_..." });`}</CodeBlock>
        </div>
        <div>
          <h3 className="mb-2 font-semibold">Create + embed a meeting</h3>
          <CodeBlock>{`const { meeting } = await MeetHub.createMeeting({ title: "Team Sync" });
await MeetHub.startMeeting(meeting.meeting_id);

MeetHub.joinMeeting({
  meetingCode: meeting.meeting_code,
  userName: "Alice",
  role: "host",
  containerId: "video-container",
  height: "640px"
});`}</CodeBlock>
        </div>
        <div>
          <h3 className="mb-2 font-semibold">Container</h3>
          <CodeBlock>{`<div id="video-container"></div>`}</CodeBlock>
        </div>
        <div>
          <h3 className="mb-2 font-semibold">Available methods</h3>
          <CodeBlock>{`MeetHub.init({ apiKey })
MeetHub.createMeeting({ title, description?, start_time?, duration?, visibility? })
MeetHub.listMeetings()
MeetHub.getMeeting(id)
MeetHub.startMeeting(id)
MeetHub.endMeeting(id)
MeetHub.issueJoinToken(id, { user_name, role })
MeetHub.joinMeeting({ meetingCode, userName, role?, containerId, token?, width?, height? })`}</CodeBlock>
        </div>
        <div className="rounded-2xl border border-dashed border-border bg-muted/20 p-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Implementation tip
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Give each product environment its own app and key so staging, QA, and production traffic stay separate.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
